/**
 * @file 权限校验 Hook
 */

import {useMemo} from 'react';
import type {PermissionCode} from '@/types/permission';
import {usePermissionsStore} from '@/store/usePermissionsStore';
import type {ProjectId} from '@/types';

interface UsePermissionOptions {
    /** 校验模式：全部满足 或 任意满足 */
    mode?: 'all' | 'any';
    /** 额外的业务条件 (ABAC) */
    condition?: boolean;
    /** 指定项目进行校验，如果不传则默认校验最近操作过的/当前被激活加载的项目 */
    projectId?: ProjectId;
}

/**
 * 增强型权限校验 Hook
 * @param code 原子权限码或数组
 * @param options 配置项
 * @example
 * ```typescript
 * // 单权限校验
 * const hasPermission = usePermission('asset:view');
 *
 * // 多权限校验（任意满足）
 * const hasPermission = usePermission(['asset:view', 'asset:edit']);
 *
 * // 多权限校验（全部满足）
 * const hasPermission = usePermission(['asset:view', 'asset:edit'], { mode: 'all' });
 *
 * // 结合业务条件 (ABAC)
 * const hasPermission = usePermission('asset:edit', { condition: asset.status !== 'APPROVED' });
 *
 * // 指定项目校验
 * const hasPermission = usePermission('asset:edit', { projectId: 1234 });
 * ```
 */
export const usePermission = (
    code: PermissionCode | PermissionCode[],
    options: UsePermissionOptions = {},
): boolean => {
    const {mode = 'any', condition = true, projectId} = options;

    // 获取当前项目权限
    const currentProjectId = usePermissionsStore((state) => state.currentProjectId);
    const targetProjectId = projectId ?? currentProjectId;
    const projectPermissions = usePermissionsStore((state) =>
        targetProjectId ? state.permissionsByProject[targetProjectId] : undefined,
    );

    const isLoading = usePermissionsStore((state) => state.isLoading);

    const codes = useMemo(() => (Array.isArray(code) ? code : [code]), [code]);

    const hasPermission = useMemo(() => {
        // 如果没有传入权限码，且没有目标项目，视为无权限
        if (codes.length === 0 || !targetProjectId) return false;

        // 如果正在加载权限或权限数据未就绪，视为无权限
        // 注意：error 不参与此处判断——有旧缓存时优先复用旧权限降级
        if (isLoading || !projectPermissions) return false;

        // 基础权限码校验 (RBAC)
        const permissionMatch =
            mode === 'all'
                ? codes.every((c) => projectPermissions.has(c))
                : codes.some((c) => projectPermissions.has(c));

        // 组合业务逻辑校验 (ABAC)
        return permissionMatch && condition;
    }, [isLoading, codes, targetProjectId, projectPermissions, mode, condition]);

    return hasPermission;
};
